import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import type { User, Session } from '@supabase/supabase-js';
import type { PortalUser } from '@/types/crm';

export function usePortalAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch portal user data - graceful handling of missing records
  const fetchPortalUser = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('portal_users')
        .select('*')
        .eq('id', userId)
        .maybeSingle(); // Use maybeSingle() to gracefully handle 0 results

      if (error) {
        // Only log if it's not a "no rows" error (PGRST116)
        if (error.code !== 'PGRST116') {
          console.error('[usePortalAuth] Error fetching portal user:', error);
        }
        return null;
      }

      if (!data) {
        // This is expected for CRM users who don't have portal access
        console.log('[usePortalAuth] No portal user record for auth user:', userId);
        return null;
      }

      console.log('[usePortalAuth] Portal user found:', data.email, data.role);
      return data as PortalUser;
    } catch (err) {
      console.error('[usePortalAuth] Unexpected error:', err);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer portal user fetch
        if (session?.user) {
          setTimeout(() => {
            fetchPortalUser(session.user.id).then(setPortalUser);
          }, 0);
        } else {
          setPortalUser(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchPortalUser(session.user.id).then(setPortalUser).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        title: 'Fout bij inloggen',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const resetPasswordForEmail = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/set-password`,
    });

    if (error) {
      toast({
        title: 'Fout bij wachtwoord reset',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }

    toast({
      title: 'Check je email',
      description: 'We hebben je een wachtwoord reset link gestuurd',
    });
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();

    if (error) {
      toast({
        title: 'Fout bij uitloggen',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateLastLogin = async () => {
    if (!user) return;

    await supabase
      .from('portal_users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', user.id);
  };

  return {
    user,
    session,
    portalUser,
    loading,
    signInWithPassword,
    resetPasswordForEmail,
    signOut,
    updateLastLogin,
    isInspector: portalUser?.role === 'inspector',
    isClient: portalUser?.role === 'client',
    hasPortalAccess: !!portalUser,
  };
}
